import React from "react";
import { ArrowUpRight, Timer, Target, Activity } from "lucide-react";

const PROGRAMS = [
  {
    id: "premium-call",
    title: "Premium Online Coaching + Weekly Call",
    price: "$120",
    cadence: "per month USD",
    icon: Timer,
    badge: "Most support",
    desc: "Everything in Premium, plus a weekly phone call with Ashley to talk through your training.",
    bullets: [
      "Initial phone consultation",
      "Personalised program delivered via Final Surge",
      "Workouts sent directly to watch",
      "Daily direct WhatsApp access to Ashley",
      "Training modifications as required",
      "Weekly phone call to discuss training (10–15 minutes)",
    ],
    image: "/programs/ashley.jpg",
    credit: {
      name: "@julesmutes",
      author_url: "https://instagram.com/julesmutes",
      photo_url: "https://instagram.com/julesmutes",
      platform: "Instagram",
    },
  },
  {
    id: "premium",
    title: "Premium Online Coaching",
    price: "$70",
    cadence: "per month USD",
    icon: Activity,
    badge: "Most popular",
    desc: "Direct daily access to your coach. Fully personalised, fully supported.",
    bullets: [
      "Initial phone consultation",
      "Personalised program delivered via Final Surge",
      "Workouts sent directly to watch",
      "Daily direct WhatsApp access to Ashley",
      "Training modifications as required",
    ],
    image: "/programs/online.jpg",
    credit: {
      name: "Joaquín Torres García",
      author_url: "https://unsplash.com/@jtorresgarcia_uy?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText",
      photo_url: "https://unsplash.com/photos/a-blurry-photo-of-a-person-running-down-a-street-SzAyWcs483w?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText",
    },
  },
  {
    id: "online",
    title: "Online Coaching",
    price: "$55",
    cadence: "per month USD",
    icon: Target,
    badge: "Weekly check-ins",
    desc: "Structured, personalised coaching with a weekly check-in to keep you progressing.",
    bullets: [
      "Initial phone consultation",
      "Personalised program delivered via Final Surge",
      "Workouts sent directly to watch",
      "Weekly WhatsApp check in",
      "Weekly training revision & modification",
    ],
    image: "/programs/sub20.jpg",
    credit: {
      name: "Jorgen Hendriksen",
      author_url: "https://unsplash.com/@jor9en?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText",
      photo_url: "https://unsplash.com/photos/a-blurred-person-is-riding-a-bicycle-BomU_atAAKQ?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText",
    },
  },
];

const NOTES = [
  "All programs are delivered on a fortnightly basis.",
  "All payments are made on a monthly basis through the Final Surge training platform.",
  "All programs are priced in USD. Payments will be automatically converted to your local currency.",
];

const goToContact = (goal) => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
  // Pre-fill goal via custom event
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("prefill-contact", { detail: { goal } }));
  }, 400);
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32 border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeader
          number="01"
          eyebrow="1:1 Coaching"
          title={<>Your next PB starts <span className="text-brand-red">here.</span></>}
          subtitle="All programs are built around your life, running history and goal race. I'll guide you through your program and get you confident for race day."
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-y-0 lg:gap-x-8">
          {PROGRAMS.map((p, i) => (
            <ProgramCard key={p.id} program={p} index={i} />
          ))}
        </div>

        <div
          data-testid="program-notes"
          className="mt-8 bg-[#0e0e10] border border-white/5 p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6"
        >
          <div className="lg:col-span-3 flex items-start gap-4">
            <span className="font-mono text-brand-red text-xs tracking-widest">NB</span>
            <span className="label-eyebrow">Good to know</span>
          </div>
          <ul className="lg:col-span-9 space-y-3">
            {NOTES.map((n, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-white/70 leading-relaxed">
                <span className="mt-2 h-1.5 w-1.5 bg-brand-red shrink-0" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ number, eyebrow, title, subtitle }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
      <div className="lg:col-span-3 flex items-start gap-4">
        <span className="font-mono text-brand-red text-xs tracking-widest">{number}</span>
        <span className="label-eyebrow">{eyebrow}</span>
      </div>
      <div className="lg:col-span-9">
        <h2 className="font-display uppercase text-white text-5xl lg:text-7xl leading-[0.9] tracking-tight max-w-3xl">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-6 max-w-2xl text-white/60 text-base lg:text-lg leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

function ProgramCard({ program, index }) {
  return (
    <article
      data-testid={`program-card-${program.id}`}
      className="group relative flex flex-col md:grid md:grid-rows-subgrid md:row-span-6 bg-[#0e0e10] border border-white/5 hover:border-brand-red/50 transition-all duration-300 overflow-hidden"
    >
      <div className="relative h-52 overflow-hidden border-b border-white/5">
        <img
          src={program.image}
          alt={program.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e10] via-black/40 to-black/10" />
        <div className="absolute top-5 left-6 right-6 flex items-center justify-between">
          <span className="font-mono text-brand-red text-xs tracking-widest">
            P/{String(index + 1).padStart(2, "0")}
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-brand-red border border-brand-red/40 bg-black/60 px-3 py-1">
            {program.badge}
          </span>
        </div>
        {program.credit && (
          <div className="absolute bottom-2 right-3 text-[9px] uppercase tracking-[0.18em] text-white/55 font-mono">
            Photo ·{" "}
            <a href={program.credit.author_url} target="_blank" rel="noreferrer" className="hover:text-white hover:underline">
              {program.credit.name}
            </a>{" "}
            /{" "}
            <a href={program.credit.photo_url} target="_blank" rel="noreferrer" className="hover:text-white hover:underline">
              {program.credit.platform || "Unsplash"}
            </a>
          </div>
        )}
      </div>

      <h3 className="px-7 lg:px-8 pt-7 lg:pt-8 font-display uppercase text-white text-3xl lg:text-4xl leading-[0.95] tracking-tight">
        {program.title}
      </h3>

      <div className="px-7 lg:px-8 mt-6 flex items-end gap-3" data-testid={`program-price-${program.id}`}>
        <span className="font-display text-5xl lg:text-6xl text-white leading-none tracking-wide">
          {program.price}
        </span>
        <span className="mb-1 text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">
          {program.cadence}
        </span>
      </div>

      <p className="px-7 lg:px-8 mt-5 text-white/65 text-sm leading-relaxed">{program.desc}</p>

      <ul className="mx-7 lg:mx-8 mt-6 space-y-3 border-t border-white/10 pt-6">
        {program.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-white/80">
            <span className="mt-2 h-1.5 w-1.5 bg-brand-red shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="px-7 lg:px-8 pt-10 pb-7 lg:pb-8 mt-auto">
        <button
          onClick={() => goToContact(program.title)}
          data-testid={`program-cta-${program.id}`}
          className="inline-flex items-center gap-2 text-white border-b border-white/20 hover:border-brand-red hover:text-brand-red transition-colors pb-1 text-sm uppercase tracking-[0.18em] font-bold"
        >
          Apply for this program <ArrowUpRight size={16} />
        </button>
      </div>
    </article>
  );
}
